import type { ReportTemplatesApi } from 'v5/api/report-templates/reportTemplates.api';
import { reportScopeWorkspaceId } from 'v5/core/reporting/reportScope';
import { WithServices } from 'v5/platform/ts/services';
import { WithErrorService } from 'v5/services/front/ErrorService/ErrorService';
import { WithMessageService } from 'v5/services/internal/MessageService/MessageService';
import type { ReportTemplateMutationService } from './ReportTemplateMutationService';

export function createReportTemplateMutationService(
  config: WithServices<[WithErrorService, WithMessageService]> & {
    api: ReportTemplatesApi;
    /** The viewer's IANA timezone: relative date filters are resolved in it. */
    getTimezone: () => string;
  }
): ReportTemplateMutationService {
  const {
    api,
    getTimezone,
    services: { errorService, messageService }
  } = config;
  const interceptor = errorService.withCategory(
    'ReportTemplateMutationService'
  );

  const reportEffect = (entityId: number, type: 'create' | 'update') =>
    messageService.reportSystemEffect.sendRequest(
      { scope: [{ entity: 'reportTemplate', entityId, payload: { type } }] },
      3000
    );

  return {
    createTemplate: async draft => {
      const template = await interceptor.wrap(() =>
        api.createTemplate(draft)
      )();
      await reportEffect(template.id, 'create');
      return template;
    },
    updateTemplate: async (id, draft) => {
      const template = await interceptor.wrap(() =>
        api.updateTemplate(id, draft)
      )();
      await reportEffect(id, 'update');
      return template;
    },
    setTemplateArchived: async (id, archived) => {
      await interceptor.wrap(() => api.setTemplateArchived(id, archived))();
      await reportEffect(id, 'update');
    },
    exportTemplate: (id, scope) =>
      interceptor.wrap(() =>
        api.exportTemplate(id, {
          timezone: getTimezone(),
          enterpriseWorkspaceId: reportScopeWorkspaceId(scope)
        })
      )()
  };
}
